import React, { useMemo, useRef } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { QuestionCircle } from 'react-bootstrap-icons';
import './InfoTip.scss';

const GAP = 6; // Tooltip.TOOLTIP_OFFSET's distance

/**
 * The app's "?" explainer: a small help mark after a label that shows a short
 * note on hover or keyboard focus. The note opens ABOVE the label (below when
 * there's no room) and never covers it — not even the first line of a label
 * that wraps — and it wears the same themed surface as the Compatibility ⓘ
 * notes (StatInfo).
 *
 * The mark is a real button: reachable with Tab, and — inside a <label> —
 * clicking it doesn't flip the switch the label belongs to.
 *
 * @param {React.ReactNode} text  the note (a sentence or two)
 * @param {string} [about]        what it explains, for screen readers ("Deep analysis")
 */
const InfoTip = ({ text, about, className = '' }) => {
    const markRef = useRef(null);
    const popperConfig = useMemo(() => ({
        modifiers: [{
            name: 'offset',
            options: {
                // clear the whole label, not just the mark: when a label wraps,
                // its first line sits above the mark
                offset: ({ placement }) => {
                    const mark = markRef.current;
                    const label = mark?.closest('label') || mark?.parentElement;
                    if (!mark || !label) return [0, GAP];
                    const m = mark.getBoundingClientRect();
                    const l = label.getBoundingClientRect();
                    const extra = placement.startsWith('top') ? m.top - l.top : placement.startsWith('bottom') ? l.bottom - m.bottom : 0;
                    return [0, GAP + Math.max(0, Math.round(extra))];
                },
            },
        }],
    }), []);
    return (
        <OverlayTrigger
            placement="top"
            delay={{ show: 120, hide: 60 }}
            popperConfig={popperConfig}
            overlay={<Tooltip className="mg-infotip">{text}</Tooltip>}
        >
            <button
                ref={markRef}
                type="button"
                className={`mg-infotip-trigger${className ? ` ${className}` : ''}`}
                aria-label={about ? `About ${about}` : 'More info'}
                // a click inside <label for=…> would otherwise toggle its control
                onClick={(e) => e.preventDefault()}
            >
                <QuestionCircle aria-hidden="true" focusable="false" />
            </button>
        </OverlayTrigger>
    );
};

export default InfoTip;
